import { AuthQueryProvider } from '@daveyplate/better-auth-tanstack';
import { AuthUIProviderTanstack } from '@daveyplate/better-auth-ui/tanstack';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Link, RouterProvider } from '@tanstack/react-router';
import { useEffect } from 'react';

import { ErrorFallback } from '@/components/ErrorFallback';
import { SubscriptionSyncHandler } from '@/components/SubscriptionSyncHandler';
import { authClient } from '@/lib/authClient';
import { betterAuthLocalizationRo } from '@/localization/betterAuthLocalizationRo';

import { AppInitGate } from './AppInitGate';
import { FilterProvider } from './contexts/FilterContext';
import { track } from './lib/mixpanel';
import { queryClient } from './lib/queryClient';
import { initSentry } from './lib/sentry';
import { router } from './router';

async function initApp() {
  await initSentry();
}

function App() {
  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', (event) => {
      track('PageView', { path: event.toLocation.pathname });

      // Scroll to top when navigating to a different page
      if (event.fromLocation?.pathname !== event.toLocation.pathname) {
        window.scrollTo(0, 0);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <AppInitGate init={initApp}>
      <Sentry.ErrorBoundary
        fallback={(errorData) => <ErrorFallback {...errorData} />}>
        <QueryClientProvider client={queryClient}>
          <AuthQueryProvider>
            <AuthUIProviderTanstack
              authClient={authClient}
              navigate={(href) => router.navigate({ href })}
              replace={(href) => router.navigate({ href, replace: true })}
              Link={({ href, ...props }) => <Link to={href} {...props} />}
              social={{ providers: ['google'] }}
              credentials={{
                forgotPassword: false,
                confirmPassword: true,
                passwordValidation: { minLength: 8 },
              }}
              account={{ basePath: '/account' }}
              localization={betterAuthLocalizationRo}
              signUp={true}>
              <FilterProvider>
                <SubscriptionSyncHandler />
                <RouterProvider router={router} />
              </FilterProvider>
            </AuthUIProviderTanstack>
          </AuthQueryProvider>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </AppInitGate>
  );
}

export default App;
